import { RouteLocationNormalized, Router } from 'vue-router'

import { ClientApp } from '@/models/client/models'
import store from '@/store'

export function routeRequiresList(
  route: RouteLocationNormalized,
  list: string
) {
  return route.meta.requiresList && route.meta.requiresList[list] === true
}

export function routeIsDetail(route: RouteLocationNormalized) {
  return route.meta.hasOwnProperty('detailOf')
}

export function routeIsDetailOf(
  route: RouteLocationNormalized,
  parameter: string
) {
  return route.meta.detailOf === parameter
}

export function getRouteIsDetailOf(route: RouteLocationNormalized): string {
  if (routeIsDetail(route)) {
    return route.meta.detailOf as string
  } else {
    throw new Error(`Not a detail route: ${route.fullPath}`)
  }
}

/**
 * Query parameters are optional and added to the url via ?product=
 * @param route
 * @param parameter
 */
export function routeHasQuery(
  route: RouteLocationNormalized,
  parameter: string
) {
  return route.meta.hasQuery && route.meta.hasQuery[parameter] === true
}

/**
 * Check whether route has required or optional parameter
 * @param route
 * @param parameter
 */
export function routeHasParam(
  route: RouteLocationNormalized,
  parameter: string
) {
  return routeIsDetailOf(route, parameter) || routeHasQuery(route, parameter)
}

export function routePage(route: RouteLocationNormalized): number {
  return Number.parseInt(route.query.page as string, 10) || 1
}

export function routePageSize(route: RouteLocationNormalized): number {
  return Number.parseInt(route.query.page_size as string, 10)
}

export function routeOrderBy(
  route: RouteLocationNormalized,
  parameter: string
): string | string[] {
  return route.query.order_by
}

export function routeHasClientApp(route: RouteLocationNormalized): boolean {
  return Boolean(route.params.app_handle)
}

export function getClientAppOfRoute(route: RouteLocationNormalized): ClientApp {
  let clientApp
  try {
    clientApp = store.getters['global/objectByProperty'](
      'client-app',
      'handle',
      route.params.app_handle
    )
  } catch (error) {
    throw new Error('Could not determine client app of route', error)
  }
  return clientApp
}

/**
 * Generate a location object including query parameters.
 * If the store contains an object for any of the route
 * parameters ('meta.hasQuery') it is put into the
 * query string of the generated location object. Like this
 * the current 'context' is propagated via query params
 * to the next location.
 * @param routeName the route name to generate a location for
 * @param router router
 */
export function getLocationPreservingState(
  routeName: string,
  router: Router,
  app_handle?: string
) {
  const query = {}
  // find parameters of target route and add to query if object (e.g. product) is selected in global state
  const routeLocation = router.resolve({
    name: routeName,
    params: {
      ...router.currentRoute.value.params,
      app_handle: app_handle
        ? app_handle
        : store.getters['global/object']('client-app').handle,
    },
  })

  if (routeLocation && routeLocation.meta.hasQuery) {
    for (const parameter in routeLocation.meta.hasQuery as any) {
      const obj = store.getters[`global/object`](parameter)
      if (obj) {
        query[parameter] = obj.id
      }
    }
  }

  return {
    name: routeName,
    params: {
      org_slug: store.getters['global/object']('organisation').slug,
      app_handle: app_handle
        ? app_handle
        : store.getters['global/object']('client-app').handle,
    },
    query,
  }
}
